import { memo, useMemo } from "react";
import { Copy } from "lucide-react";
import { type ColumnDef, type ColumnFiltersState, type SortingState } from "@tanstack/react-table";
import { DataTable } from "@/ui/molecules";
import { DealStatusCell } from "./DealStatusCell";
import { formatDate } from "@/utils/utils";
import type { TDeal } from "@/store";

// Lookup types for displaying names instead of IDs
type TEntityLookup = { id: string; name: string; code?: string };

export interface TDealsTableProps {
    deals: TDeal[];
    loading?: boolean;
    onDealClick?: (deal: TDeal) => void;
    className?: string;
    serviceProviders?: TEntityLookup[];
    portfolioCompanies?: TEntityLookup[];
    /** Persist user-adjusted column widths under this key. */
    storageKey?: string;
    columnFilters?: ColumnFiltersState;
    onColumnFiltersChange?: (f: ColumnFiltersState) => void;
    sortingState?: SortingState;
    onSortingChange?: (s: SortingState) => void;
    rowCount?: { filtered: number; total: number; isFiltered: boolean };
    /** When true, the Status column renders a clickable activate/deactivate toggle. */
    canToggleActive?: boolean;
    /** Called when the user toggles a deal's active status. */
    onToggleActive?: (deal: TDeal) => void | Promise<void>;
    /** When set, each row gets a "Copy deal" action. */
    onCopyDeal?: (deal: TDeal) => void;
}

export const DealsTable = memo(function DealsTable({
    deals,
    loading,
    onDealClick,
    className,
    serviceProviders = [],
    portfolioCompanies = [],
    storageKey,
    columnFilters,
    onColumnFiltersChange,
    sortingState,
    onSortingChange,
    rowCount,
    canToggleActive,
    onToggleActive,
    onCopyDeal,
}: TDealsTableProps) {
    // Create lookup maps for efficient name resolution
    const spLookup = useMemo(
        () => new Map(serviceProviders.map((sp) => [sp.id, sp])),
        [serviceProviders],
    );
    const pcLookup = useMemo(
        () => new Map(portfolioCompanies.map((pc) => [pc.id, pc])),
        [portfolioCompanies],
    );

    // Build a combined filter option list covering both business partner types
    const bpFilterOptions = useMemo(
        () => [
            ...serviceProviders.map((sp) => ({ value: sp.id, label: sp.name })),
            ...portfolioCompanies.map((pc) => ({ value: pc.id, label: pc.name })),
        ],
        [serviceProviders, portfolioCompanies],
    );
    const pcFilterOptions = useMemo(
        () => portfolioCompanies.map((pc) => ({ value: pc.id, label: pc.name })),
        [portfolioCompanies],
    );

    const columns: ColumnDef<TDeal>[] = useMemo(
        () => [
            {
                accessorKey: "name",
                header: "Name",
                cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
                meta: {
                    filterType: "text",
                    filterPlaceholder: "Search name...",
                },
            },
            {
                id: "businessPartnerName",
                header: "Business Partner Name",
                accessorFn: (deal) => {
                    if (deal.serviceProviderId) {
                        return spLookup.get(deal.serviceProviderId)?.name ?? "";
                    }
                    if (deal.portfolioCompanyId) {
                        return pcLookup.get(deal.portfolioCompanyId)?.name ?? "";
                    }
                    return "";
                },
                cell: ({ row }) => {
                    const { serviceProviderId, portfolioCompanyId } = row.original;
                    const name = serviceProviderId
                        ? spLookup.get(serviceProviderId)?.name
                        : portfolioCompanyId
                          ? pcLookup.get(portfolioCompanyId)?.name
                          : null;
                    return name ? (
                        <span className="text-sm">{name}</span>
                    ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                    );
                },
                meta: {
                    filterType: bpFilterOptions.length > 0 ? "select" : "text",
                    filterOptions: bpFilterOptions,
                    filterPlaceholder: "All business partners",
                },
            },
            {
                accessorKey: "portfolioCompanyId",
                header: "Portfolio Company",
                cell: ({ row }) => {
                    const pcId = row.original.portfolioCompanyId;
                    if (!pcId) return <span className="text-muted-foreground text-sm">—</span>;
                    const pc = pcLookup.get(pcId);
                    return pc ? (
                        <div>
                            <span className="text-sm">{pc.name}</span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                    );
                },
                meta: {
                    filterType: pcFilterOptions.length > 0 ? "select" : "text",
                    filterOptions: pcFilterOptions,
                    filterPlaceholder: "All companies",
                },
            },
            {
                accessorKey: "dealFileType",
                header: "File Type",
                cell: ({ row }) => (
                    <span className="uppercase text-xs font-medium">
                        {row.original.dealFileType}
                    </span>
                ),
                meta: {
                    filterType: "select",
                    filterOptions: [
                        { value: "tb_only", label: "TB Only" },
                        { value: "tb_and_gl", label: "TB & GL" },
                    ],
                    filterPlaceholder: "All types",
                },
            },
            {
                accessorKey: "monthlyDeadlineDay",
                header: "Deadline",
                cell: ({ row }) =>
                    row.original.monthlyDeadlineDay
                        ? `Day ${row.original.monthlyDeadlineDay}`
                        : "—",
            },
            {
                accessorKey: "isActive",
                header: "Status",
                cell: ({ row }) => (
                    <DealStatusCell
                        deal={row.original}
                        canToggle={!!canToggleActive}
                        onToggle={onToggleActive}
                    />
                ),
                meta: {
                    filterType: "boolean",
                    filterPlaceholder: "All statuses",
                },
            },
            {
                accessorKey: "createdAt",
                header: "Created",
                cell: ({ row }) => formatDate(row.original.createdAt),
                meta: {
                    filterType: "date",
                },
            },
            ...(onCopyDeal
                ? [
                      {
                          id: "actions",
                          header: "",
                          enableSorting: false,
                          cell: ({ row }) => (
                              <div className="flex items-center justify-end">
                                  <button
                                      type="button"
                                      onClick={(e) => {
                                          e.stopPropagation();
                                          onCopyDeal(row.original);
                                      }}
                                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                      title="Copy deal"
                                      aria-label={`Copy deal ${row.original.name}`}
                                      data-testid={`copy-deal-${row.original.id}`}
                                  >
                                      <Copy className="h-4 w-4" />
                                  </button>
                              </div>
                          ),
                      } satisfies ColumnDef<TDeal>,
                  ]
                : []),
        ],
        [
            spLookup,
            pcLookup,
            bpFilterOptions,
            pcFilterOptions,
            canToggleActive,
            onToggleActive,
            onCopyDeal,
        ],
    );

    return (
        <DataTable
            columns={columns}
            data={deals}
            loading={loading}
            onRowClick={onDealClick}
            emptyMessage="No deals found."
            className={className}
            storageKey={storageKey}
            manualFiltering
            manualSorting
            initialColumnFilters={columnFilters}
            onColumnFiltersChange={onColumnFiltersChange}
            sortingState={sortingState}
            onSortingChange={onSortingChange}
            rowCount={rowCount}
            rowNoun={{ one: "deal", many: "deals" }}
        />
    );
});
